/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ConnectorFlowDTO,
  ConnectorFlowInstanceDTO,
  ConnectorFlowInstanceJobStatusDTO,
  ConnectorInstanceDTO,
  ConnectorInstanceSafeDTO,
  CreateConnectorInstanceArgs,
  CreateConnectorInstanceRequest,
  CreateConnectorInstanceResponse,
  CreateFlowInstanceArgs,
  CreateFlowInstanceRequest,
  CustomConnectorDto,
  DeleteConnectorFlowInstanceArgs,
  DeleteConnectorInstanceArgs,
  GetArchivedConnectorFlowInstanceJobsArgs,
  GetConnectorFlowArgs,
  GetConnectorFlowInstanceJobsArgs,
  GetConnectorFlowInstancesArgs,
  GetConnectorFlowsArgs,
  GetConnectorInstanceArgs,
  GetConnectorInstancesArgs,
  GetConnectorInstanceWithCredentialsArgs,
  GetLatestJobStatusesArgs,
  GetGupshupOnboardingArgs,
  GetWorkspaceConnectorArgs,
  GupshupOnboardingDTO,
  PrepareConnectorFlowArgs,
  PrepareConnectorFlowStepArgs,
  PrepareConnectorFlowStepResponse,
  PreviewConnectorFlowArgs,
  SourceConnectorJobDTO,
  SourceRecord,
  TestConnectionResponse,
  TestConnectorConnectionArgs,
} from '@/models/connectors';
import { setI18nMap } from '@/data/translationMapper';
import { audiencerateApi } from '@/services/audiencerate';
import { translateI18nValues } from '@/utils/translationUtil';

export const workspaceConnectorsApi = audiencerateApi.injectEndpoints({
  endpoints: (build) => ({
    getConnectorTranslations: build.query<Record<string, Record<string, string>>, void>({
      query: () => ({
        url: `/v1/connectors/translations`,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        const { data } = await queryFulfilled;
        setI18nMap(data);
      },
    }),

    getConnectorInstances: build.query<ConnectorInstanceSafeDTO[], GetConnectorInstancesArgs>({
      query: ({ workspaceId }) => ({
        url: `/v1/workspace/${workspaceId}/connectors/instance`,
      }),
      providesTags: ['ConnectorInstances'],
    }),

    createConnectorInstance: build.mutation<CreateConnectorInstanceResponse, CreateConnectorInstanceArgs & CreateConnectorInstanceRequest>({
      query: ({ workspaceId, lang, ...body }) => ({
        url: `/v1/workspace/${workspaceId}/connectors/instance`,
        method: 'POST',
        body,
      }),
      transformErrorResponse: (response: any, meta, arg) => {
        return translateI18nValues(response, arg.lang);
      },
      invalidatesTags: ['ConnectorInstances', 'Connectors'],
    }),

    getConnectorInstance: build.query<ConnectorInstanceSafeDTO, GetConnectorInstanceArgs>({
      query: ({ workspaceId, id }) => ({
        url: `/v1/workspace/${workspaceId}/connectors/instance/${id}`,
      }),
      providesTags: (result, error, args) => [{ type: 'ConnectorInstances', id: args.id }],
    }),

    deleteConnectorInstance: build.mutation<void, DeleteConnectorInstanceArgs>({
      query: ({ workspaceId, id, force }) => ({
        url: `/v1/workspace/${workspaceId}/connectors/instance/${id}`,
        method: 'DELETE',
        params: force ? { force: 'true' } : undefined,
      }),
      invalidatesTags: ['ConnectorInstances', 'Connectors'],
    }),

    getConnectorInstanceWithCredentials: build.query<ConnectorInstanceDTO, GetConnectorInstanceWithCredentialsArgs>({
      query: ({ workspaceId, id }) => ({
        url: `/v1/workspace/${workspaceId}/connectors/instance/${id}/creds`,
      }),
      providesTags: (result, error, args) => [{ type: 'ConnectorInstances', id: args.id }],
    }),

    getGupshupOnboarding: build.query<GupshupOnboardingDTO, GetGupshupOnboardingArgs>({
      query: ({ workspaceId, connectorInstanceId }) => ({
        url: `/v1/workspace/${workspaceId}/connectors/instance/${connectorInstanceId}/gupshup/onboarding`,
      }),
      providesTags: (result, error, args) => [{ type: 'ConnectorInstances', id: args.connectorInstanceId }],
    }),

    getWorkspaceConnector: build.query<CustomConnectorDto, GetWorkspaceConnectorArgs>({
      query: ({ workspaceId, id }) => ({
        url: `/v1/workspace/${workspaceId}/connectors/${id}`,
      }),
      transformResponse: (response: CustomConnectorDto, meta, arg) => {
        return translateI18nValues(response, arg.lang);
      },
      transformErrorResponse: (response: any, meta, arg) => {
        return translateI18nValues(response, arg.lang);
      },
      providesTags: (result, error, args) => [{ type: 'Connectors', id: args.id }],
    }),

    getWorkspaceConnectors: build.query<CustomConnectorDto[], GetConnectorFlowsArgs>({
      query: ({ workspaceId, queryParams }) => {
        const params =
          queryParams == null ? undefined : Object.fromEntries(Object.entries(queryParams).filter(([, value]) => value !== undefined && value !== ''));
        const hasParams = params && Object.keys(params).length > 0;
        return {
          url: `/v1/workspace/${workspaceId}/connectors`,
          ...(hasParams ? { params } : {}),
        };
      },
      transformResponse: (response: CustomConnectorDto[], meta, arg) => {
        return translateI18nValues(response, arg.lang);
      },
      transformErrorResponse: (response: any, meta, arg) => {
        return translateI18nValues(response, arg.lang);
      },
      providesTags: ['Connectors'],
    }),

    getConnectorFlows: build.query<ConnectorFlowDTO[], GetConnectorFlowsArgs>({
      query: ({ workspaceId, queryParams }) => {
        const params =
          queryParams == null ? undefined : Object.fromEntries(Object.entries(queryParams).filter(([, value]) => value !== undefined && value !== ''));
        const hasParams = params && Object.keys(params).length > 0;
        return {
          url: `/v1/workspace/${workspaceId}/connectorflow`,
          ...(hasParams ? { params } : {}),
        };
      },
      transformResponse: (response: ConnectorFlowDTO[], meta, arg) => {
        return translateI18nValues(response, arg.lang);
      },
      transformErrorResponse: (response: any, meta, arg) => {
        return translateI18nValues(response, arg.lang);
      },
      providesTags: ['ConnectorFlows'],
    }),

    getConnectorFlow: build.query<ConnectorFlowDTO, GetConnectorFlowArgs>({
      query: ({ workspaceId, id }) => ({
        url: `/v1/workspace/${workspaceId}/connectorflow/${id}`,
      }),
      providesTags: (result, error, args) => [{ type: 'ConnectorFlows', id: args.id }],
    }),

    prepareConnectorFlow: build.query<ConnectorFlowDTO, PrepareConnectorFlowArgs>({
      query: ({ workspaceId, id, audienceId }) => ({
        url: `/v1/workspace/${workspaceId}/connectorflow/${id}/prepare`,
        ...(audienceId ? { params: { audienceId } } : {}),
      }),
      providesTags: (result, error, args) => [{ type: 'ConnectorFlows', id: args.id }],
    }),

    prepareConnectorFlowStep: build.mutation<PrepareConnectorFlowStepResponse, PrepareConnectorFlowStepArgs>({
      query: ({ workspaceId, flowId, stepId, audienceId, connectorInstanceId, stepData, direction }) => ({
        url: `/v1/workspace/${workspaceId}/connectorflow/${flowId}/prepare/${stepId}`,
        method: 'POST',
        ...(audienceId ? { params: { audienceId } } : {}),
        body: stepData,
        ...(direction !== 'INPUT' && connectorInstanceId
          ? {
              headers: {
                'X-Connector-Instance-Id': connectorInstanceId,
              },
            }
          : {}),
      }),
      // Same reason as createFlowInstance: a flow that fails while building the form answers with
      // its own i18n keys.
      transformErrorResponse: (response: any, meta, arg) => {
        return translateI18nValues(response, arg.lang);
      },
    }),

    prepareConnectorFlowStep1: build.query<PrepareConnectorFlowStepResponse, { workspaceId: string; flowId: string; audienceId?: string }>({
      query: ({ workspaceId, flowId, audienceId }) => ({
        url: `/v1/workspace/${workspaceId}/connectorflow/${flowId}/prepare/step1`,
        ...(audienceId ? { params: { audienceId } } : {}),
      }),
    }),

    createFlowInstance: build.mutation<ConnectorFlowInstanceDTO, CreateFlowInstanceArgs & CreateFlowInstanceRequest>({
      query: ({ workspaceId, id, lang, ...body }) => ({
        url: `/v1/workspace/${workspaceId}/connectorflow/${id}/instance`,
        method: 'POST',
        body,
      }),
      // A failing flow answers with the connector's own i18n keys; untranslated they reach the
      // screen verbatim, as "i18n_GUPSHUP_PARAM_FORMAT_POSITIONAL_INVALID".
      transformErrorResponse: (response: any, meta, arg) => {
        return translateI18nValues(response, arg?.lang);
      },
      invalidatesTags: ['ConnectorFlowInstances'],
    }),

    getConnectorFlowInstances: build.query<ConnectorFlowInstanceDTO[], GetConnectorFlowInstancesArgs>({
      query: ({ workspaceId, audienceId, connectorInstanceId }) => ({
        url: `/v1/workspace/${workspaceId}/connectorflow/instance`,
        params: {
          ...(audienceId ? { audienceId } : {}),
          ...(connectorInstanceId ? { connectorInstanceId } : {}),
        },
      }),
      transformResponse: (response: ConnectorFlowInstanceDTO[], meta, arg) => {
        return translateI18nValues(response, arg.lang);
      },
      transformErrorResponse: (response: any, meta, arg) => {
        return translateI18nValues(response, arg.lang);
      },
      providesTags: ['ConnectorFlowInstances'],
    }),

    deleteConnectorFlowInstance: build.mutation<void, DeleteConnectorFlowInstanceArgs>({
      query: ({ workspaceId, id }) => ({
        url: `/v1/workspace/${workspaceId}/connectorflow/instance/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ConnectorFlowInstances'],
    }),

    getLatestJobStatuses: build.query<ConnectorFlowInstanceJobStatusDTO[], GetLatestJobStatusesArgs>({
      query: ({ workspaceId, audienceId, connectorInstanceId }) => ({
        url: `/v1/workspace/${workspaceId}/connectorflow/jobs`,
        params: {
          ...(audienceId ? { audienceId } : {}),
          ...(connectorInstanceId ? { connectorInstanceId } : {}),
        },
      }),
      providesTags: ['ConnectorJobStatuses'],
    }),

    previewConnectorFlow: build.mutation<SourceRecord[], PreviewConnectorFlowArgs>({
      query: ({ workspaceId, flowId, limit = 5, flowParams }) => ({
        url: `/v1/workspace/${workspaceId}/connectorflow/${flowId}/preview`,
        method: 'POST',
        body: { limit, flowParams },
      }),
    }),

    testConnectorConnection: build.mutation<TestConnectionResponse, TestConnectorConnectionArgs>({
      query: ({ workspaceId, connectorInstanceId }) => ({
        url: `/v1/workspace/${workspaceId}/connectors/instance/${connectorInstanceId}/test-connection`,
        method: 'POST',
        body: {},
      }),
    }),

    getConnectorFlowInstanceJobs: build.query<SourceConnectorJobDTO[], GetConnectorFlowInstanceJobsArgs>({
      query: ({ workspaceId, connectorFlowInstanceId, limit = 50, offset = 0 }) => ({
        url: `/v1/workspace/${workspaceId}/connectorflow/instance/${connectorFlowInstanceId}/jobs`,
        params: { limit, offset },
      }),
      providesTags: (result, error, args) => [{ type: 'ConnectorJobStatuses', id: args.connectorFlowInstanceId }],
    }),

    getArchivedConnectorFlowInstanceJobs: build.query<SourceConnectorJobDTO[], GetArchivedConnectorFlowInstanceJobsArgs>({
      query: ({ workspaceId, archivedCnfiExternalId, limit = 50, offset = 0 }) => ({
        url: `/v1/workspace/${workspaceId}/connectorflow/instance/archived/jobs`,
        params: { archivedCnfiExternalId, limit, offset },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetConnectorTranslationsQuery,
  useGetConnectorInstancesQuery,
  useCreateConnectorInstanceMutation,
  useGetConnectorInstanceQuery,
  useDeleteConnectorInstanceMutation,
  useGetConnectorInstanceWithCredentialsQuery,
  useGetGupshupOnboardingQuery,
  useGetWorkspaceConnectorQuery,
  useGetWorkspaceConnectorsQuery,
  useGetConnectorFlowsQuery,
  useGetConnectorFlowQuery,
  usePrepareConnectorFlowQuery,
  usePrepareConnectorFlowStepMutation,
  usePrepareConnectorFlowStep1Query,
  useCreateFlowInstanceMutation,
  useGetConnectorFlowInstancesQuery,
  useGetLatestJobStatusesQuery,
  useDeleteConnectorFlowInstanceMutation,
  useLazyGetConnectorInstancesQuery,
  useLazyGetConnectorInstanceQuery,
  useLazyGetConnectorInstanceWithCredentialsQuery,
  useLazyGetWorkspaceConnectorQuery,
  useLazyGetWorkspaceConnectorsQuery,
  useLazyGetConnectorFlowsQuery,
  useLazyGetConnectorFlowQuery,
  useLazyPrepareConnectorFlowQuery,
  useLazyPrepareConnectorFlowStep1Query,
  useLazyGetConnectorFlowInstancesQuery,
  usePreviewConnectorFlowMutation,
  useTestConnectorConnectionMutation,
  useGetConnectorFlowInstanceJobsQuery,
  useLazyGetConnectorFlowInstanceJobsQuery,
  useGetArchivedConnectorFlowInstanceJobsQuery,
} = workspaceConnectorsApi;
